<template>
  <VeeForm v-slot="{ errors, handleSubmit, meta }" as="">
    <form
      autocomplete="off"
      data-testid="form"
      class="w-full"
      @submit="handleSubmit($event, createBankAccount)"
    >
      <NText
        variant="caption1"
        weight="medium"
        class="text-neutral-secondary-press block pr-3 mt-5 mb-2"
      >
        {{ t('withdrawal.shebaNumber') }}
      </NText>

      <VeeField
        v-slot="{ field }"
        v-model="formModel.bankAccountNumber"
        name="bankAccountNumber"
        :rules="{
          required: true,
          equalLength: [
            MAX_BANK_ACCOUNT_NUMBER,
            t('withdrawal.shebaNumber'),
            t('misc.digit'),
          ],
        }"
      >
        <div class="flex items-baseline">
          <NInput
            v-model="formModel.bankAccountNumber"
            v-mask="{
              alias: 'numeric',
              allowMinus: false,
              allowPlus: false,
              placeholder: '',
              radixPoint: '',
              rightAlign: false,
            }"
            v-focus
            :placeholder="t('withdrawal.insertBankAccount')"
            :input-class="
              formModel.bankAccountNumber ? 'text-left' : 'text-right'
            "
            class="w-full"
            v-bind="field"
            :error="errors.bankAccountNumber"
            show-error
            type="tel"
            maxlength="24"
            required
            :required-sign="false"
            @focusin="$event.target.select()"
          >
            <template #postfix>
              <NText
                weight="medium"
                variant=""
                class="mx-2 text-neutral-secondary"
              >
                {{ t('withdrawal.shebaPrefix') }}
              </NText>
            </template>
          </NInput>
        </div>
      </VeeField>

      <NButton
        v-gid="{
          module: 'cash',
          screen: 'home',
          section: 'addBankAccountDialog',
          component: 'submitButton',
          disabled: isCreatingBankAccount || !meta.valid,
        }"
        class="w-full mt-4"
        :loading="isCreatingBankAccount"
        :disabled="isCreatingBankAccount || !meta.valid"
        type="submit"
      >
        {{ t('common.submit_request') }}
      </NButton>
    </form>
  </VeeForm>
</template>

<script setup lang="ts">
import { reactive } from 'vue';
import { useI18n } from 'vue-i18n';
import { useDialog, useToast } from '@negar/core';
import { useCreateBankAccountQuery } from '@core/cash';
import { useQueryClient } from '@libs/use';
import { USER_QUERY_KEYS } from '@core/user';
import { sleep } from '@libs/utils';

import { MAX_BANK_ACCOUNT_NUMBER } from '@/modules/cash';

import vFocus from '@/directives/focus';
import vMask from '@/directives/mask';

const { t } = useI18n();
const toast = useToast();

const queryClient = useQueryClient();

const formModel = reactive({
  bankAccountNumber: null,
});

const { remove: closeAddBankAccountDialog } = useDialog();

const { createBankAccount, isCreatingBankAccount } = useCreateBankAccountQuery({
  async onSuccess() {
    toast.neutral({
      message: t('withdrawal.createBankAccountMessage'),
      duration: 5000,
    });

    closeAddBankAccountDialog();

    await sleep(5000);

    queryClient.invalidateQueries(USER_QUERY_KEYS.userBankAccounts);
  },
  onReject(error) {
    if (error) {
      for (const { message } of error.response.data) {
        toast.negative({
          message,
          duration: 5000,
        });
      }
    }
  },
});
</script>
